import React from "react";
import { useTranslation } from "react-i18next";
import { Clock, Ban, Lock, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BLOCK_ICON, hhmm } from "./constants";

export function BlockDialog({ draft, setDraft, onSubmit }) {
  const { t, i18n } = useTranslation();
  const LOC = i18n.language || "en";
  return (
    <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
      <DialogContent className="rounded-[0.7rem] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ban size={17} className="text-indigo-500" /> {t("cal.blockTitle")}</DialogTitle>
          <DialogDescription>{t("cal.blockDesc")}</DialogDescription>
        </DialogHeader>
        {draft && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <Clock size={14} className="text-slate-400" />
              {new Date(draft.start).toLocaleDateString(LOC, { weekday: "short", month: "short", day: "numeric" })} · {hhmm(new Date(draft.start), LOC)} – {hhmm(new Date(draft.end), LOC)}
            </div>
            <div className="space-y-1.5">
              <Label>{t("cal.categoryLabel")}</Label>
              <div className="grid grid-cols-3 gap-2">
                {["leave", "absence", "personal"].map((c) => {
                  const Icon = BLOCK_ICON[c];
                  const active = draft.category === c;
                  return (
                    <button key={c} data-testid={`block-cat-${c}`} onClick={() => setDraft((b) => ({ ...b, category: c }))}
                      className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs font-medium transition-colors ${active ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                      <Icon size={16} /> {t(`cal.cat.${c}`)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("cal.blockTitleLabel")}</Label>
              <Input data-testid="block-title-input" value={draft.title} onChange={(e) => setDraft((b) => ({ ...b, title: e.target.value }))} placeholder={t("cal.blockTitlePh")} className="h-10 rounded-lg" />
            </div>
            <p className="text-xs text-slate-400 flex items-center gap-1.5"><Lock size={12} /> {t("cal.privateNote")}</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-lg" onClick={() => setDraft(null)}>{t("common.cancel")}</Button>
          <Button data-testid="block-create-btn" onClick={onSubmit} className="rounded-lg gap-1.5"><Plus size={15} /> {t("cal.createBlock")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
